import { ReactNode } from 'react';
import { Card, Badge } from '@atoms/index';
import { cn } from '@/utils/cn';

export interface DetailModalSection {
  title: string;
  icon: string;
  iconBgColor?: string;
  content: ReactNode;
  gridCols?: 1 | 2 | 3 | 4;
}

export interface DetailModalStatCard {
  label: string;
  value: string | ReactNode;
  icon: string;
  iconBgColor?: string;
  valueColor?: string;
}

export interface DetailModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  statCards?: DetailModalStatCard[];
  sections?: DetailModalSection[];
  actions?: ReactNode;
  maxWidth?: 'md' | 'lg' | 'xl' | '2xl' | '5xl';
}

export function DetailModal({
  title,
  subtitle,
  onClose,
  statCards = [],
  sections = [],
  actions,
  maxWidth = '5xl',
}: DetailModalProps) {
  const maxWidthClasses = {
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '5xl': 'max-w-5xl',
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <Card className={cn('w-full max-h-[95vh] overflow-hidden flex flex-col shadow-2xl', maxWidthClasses[maxWidth])}>
        <div className="bg-gradient-to-r from-[#E63946] to-[#C1121F] text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-1">{title}</h3>
              {subtitle && <p className="text-white/90 text-sm">{subtitle}</p>}
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 bg-[#F9FAFB]">
          <div className="space-y-6">
            {statCards.length > 0 && (
              <div
                className={cn(
                  'grid gap-4',
                  statCards.length === 1 && 'grid-cols-1',
                  statCards.length === 2 && 'grid-cols-1 md:grid-cols-2',
                  statCards.length >= 3 && 'grid-cols-1 md:grid-cols-3'
                )}
              >
                {statCards.map((stat, index) => (
                  <div key={index} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-xl flex items-center justify-center',
                          stat.iconBgColor || 'bg-blue-50'
                        )}
                      >
                        <span className="text-xl">{stat.icon}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                        <div className={cn('mt-1', stat.valueColor || 'text-gray-900')}>
                          {typeof stat.value === 'string' ? (
                            <p className={cn('text-xl font-bold', stat.valueColor)}>{stat.value}</p>
                          ) : (
                            stat.value
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {sections.map((section, index) => {
              const gridColsClass = {
                1: 'grid-cols-1',
                2: 'grid-cols-1 md:grid-cols-2',
                3: 'grid-cols-1 md:grid-cols-3',
                4: 'grid-cols-1 md:grid-cols-4',
              }[section.gridCols || 1];

              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center',
                        section.iconBgColor || 'bg-blue-50'
                      )}
                    >
                      <span className="text-lg">{section.icon}</span>
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">{section.title}</h4>
                  </div>
                  <div className={cn('grid gap-4', gridColsClass)}>{section.content}</div>
                </div>
              );
            })}

            {actions && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">{actions}</div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
